const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS = "!@#$%&*+-=?";
const ALL_CHARS = LOWER + UPPER + DIGITS + SYMBOLS;

/**
 * The backend's `@IsStrongPassword` policy, expressed once: 8–128 characters with a lowercase, an uppercase,
 * a digit and a non-alphanumeric.
 *
 * Shared by the create drawer and the set-password section so the two cannot drift, and so the message shown
 * when it fails is our translated one rather than Zod's English default — which, for a `.regex()` chain,
 * renders the pattern source itself into the UI.
 */
export const STRONG_PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,128}$/;

/**
 * Generates a password that satisfies {@link STRONG_PASSWORD_PATTERN} by construction: at least one
 * lowercase, uppercase, digit and symbol, then filled and shuffled from the full alphabet.
 *
 * Entropy comes from `crypto.getRandomValues`. The Spanish copy that used to live in this file moved to the
 * `users` i18n namespace — a util is the wrong place for user-facing strings, and it only had one locale.
 */
export function generateSecurePassword(length = 16): string {
  const size = Math.max(8, length);
  const random = new Uint8Array(size);
  crypto.getRandomValues(random);

  const result: string[] = [
    LOWER[random[0]! % LOWER.length]!,
    UPPER[random[1]! % UPPER.length]!,
    DIGITS[random[2]! % DIGITS.length]!,
    SYMBOLS[random[3]! % SYMBOLS.length]!,
  ];
  for (let i = 4; i < size; i++) {
    result.push(ALL_CHARS[random[i]! % ALL_CHARS.length]!);
  }

  // Fisher–Yates, so the guaranteed characters are not always the first four.
  const shuffle = new Uint8Array(size);
  crypto.getRandomValues(shuffle);
  for (let i = result.length - 1; i > 0; i--) {
    const j = shuffle[i]! % (i + 1);
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result.join("");
}
